import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import type {
  EmployeeSummary,
  PageResponse,
  TeamDetail,
} from "../../shared/types/api";

const schema = z.object({
  name: z.string().min(1, "Enter a name").max(150),
  teamType: z.enum(["HOME_TEAM", "DELIVERY_TEAM", "TEMPORARY_TEAM", "FUNCTIONAL_TEAM"]),
  description: z.string().max(1000).optional(),
  teamLeadId: z.string().optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "ARCHIVED"]),
});

type FormValues = z.infer<typeof schema>;

const TYPE_OPTIONS = [
  { value: "HOME_TEAM", label: "Home team" },
  { value: "DELIVERY_TEAM", label: "Delivery team" },
  { value: "TEMPORARY_TEAM", label: "Temporary team" },
  { value: "FUNCTIONAL_TEAM", label: "Functional team" },
] as const;

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active", hint: "In use and offered wherever a team is chosen." },
  {
    value: "INACTIVE",
    label: "Inactive",
    hint: "Not currently used but may return. Hidden from pickers; history still resolves.",
  },
  {
    value: "ARCHIVED",
    label: "Archived",
    hint: "Permanently retired. Kept only so historical records remain readable.",
  },
] as const;

/**
 * Edits a team.
 *
 * <p>The code is not editable. It identifies the team across every membership row and report, and
 * changing it would silently detach those references from the thing they name.
 *
 * <p>There is no delete. Teams are archived instead, because historical memberships point at them
 * and a report naming a disbanded team must still be able to name it.
 */
export function TeamEditDrawer({
  team,
  onClose,
  onSaved,
}: {
  team: TeamDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [formError, setFormError] = useState<string | null>(null);

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: Boolean(team),
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const status = watch("status");

  useEffect(() => {
    if (team) {
      reset({
        name: team.name,
        teamType: team.teamType,
        description: team.description ?? "",
        teamLeadId: team.teamLeadId ?? "",
        status: team.status,
      });
    }
  }, [team, reset]);

  const close = () => {
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    if (!team) {
      return;
    }
    setFormError(null);
    try {
      await api.patch(`/api/v1/teams/${team.id}`, {
        ...values,
        teamLeadId: values.teamLeadId || undefined,
        version: team.version,
      });
      onSaved();
      close();
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.isConcurrentModification
          ? "Someone else changed this team while you were editing. Close this panel and reopen it to see their version."
          : error instanceof ApiError
            ? error.message
            : "Could not save the changes.",
      );
    }
  });

  return (
    <Drawer
      open={Boolean(team)}
      onClose={close}
      title="Edit team"
      subtitle={team?.code}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Saving…" : "Save changes"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        <InlineAlert tone="info">
          The team code cannot be changed — it identifies this team across every membership record.
        </InlineAlert>

        <Field label="Name" required error={errors.name?.message}>
          <Input autoFocus {...register("name")} />
        </Field>

        <Field label="Type" required error={errors.teamType?.message}>
          <Select {...register("teamType")}>
            {TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Team lead" hint="Optional">
          <Select {...register("teamLeadId")} disabled={employees.isPending}>
            <option value="">No lead assigned</option>
            {(employees.data?.content ?? []).map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.fullName} ({employee.employeeCode})
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="Status"
          required
          error={errors.status?.message}
          hint={STATUS_OPTIONS.find((option) => option.value === status)?.hint}
        >
          <Select {...register("status")}>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Description" error={errors.description?.message} hint="Optional">
          <Textarea rows={3} {...register("description")} />
        </Field>
      </form>
    </Drawer>
  );
}
